import React, { useState, useImperativeHandle, forwardRef, useEffect, useRef } from 'react';
import s from './NavBar.module.sass';
import { useSimpleTranslation } from '../../hooks/useTranslation';
import dashboardImg from '../../img/dashboard-normal.svg';
import trade from '../../img/trade-normal.svg';
import markets from '../../img/markets-normal.svg';
import wallet from '../../img/wallet-normal.svg';
import broker from '../../img/broker-program-normal.svg';
import mailbox from '../../img/mailbox-normal.svg';
import setting from '../../img/settings-normal.svg';
import logo from '../../img/logo.png';
import paint from '../../img/paint.png';
import supportChat from '../../img/support-chat-normal.svg';
import exit from '../../img/exit-normal.svg';
import FlagIcon from '../FlagIcon/FlagIcon';

const NavBar = forwardRef(({ showExactMenu, setShowExactMenu, showChat, setShowChat, onCollapseChange, onOpenAdminPanel, onOpenSiteSettings, siteSettings, userRole }, ref) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const navbarRef = useRef(null);
    const navContentRef = useRef(null);
    const morphStateRef = useRef({ targetY: 0, currentY: 0, targetHeight: 0, currentHeight: 0, isMouseOver: false });
    const prevActiveMenuRef = useRef(null); // Сохраняем предыдущий активный таб
    const isAnimatingRef = useRef(false); // Флаг для отслеживания анимации перетекания
    const lastMouseMoveTimeRef = useRef(0); // Время последнего движения мыши
    const lastMousePositionRef = useRef({ x: 0, y: 0 }); // Последняя позиция мыши
    
    // Система переводов
    const { t } = useSimpleTranslation(siteSettings);
    
    // Определяем мобильное устройство
    useEffect(() => {
        let timeoutId;
        const handleResize = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                const mobile = window.innerWidth <= 768;
                setIsMobile(mobile);
                if (!mobile && isMobileOpen) {
                    setIsMobileOpen(false);
                }
            }, 150);
        };

        window.addEventListener('resize', handleResize, { passive: true });
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(timeoutId);
        };
    }, [isMobileOpen]);

    // Предоставляем методы для управления извне
    useImperativeHandle(ref, () => ({
        toggleMobileMenu: () => {
            setIsMobileOpen(prev => !prev);
        }
    }));


    // Закрываем мобильное меню при клике вне его
    useEffect(() => {
        if (!isMobileOpen) return;
        
        const handleClickOutside = (event) => {
            if (navbarRef.current && !navbarRef.current.contains(event.target)) {
                setIsMobileOpen(false);
            }
        };

        // Используем capture фазу для лучшей производительности
        document.addEventListener('mousedown', handleClickOutside, true);
        document.addEventListener('touchstart', handleClickOutside, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside, true);
            document.removeEventListener('touchstart', handleClickOutside, true);
        };
    }, [isMobileOpen]);

    // Эффект морфинга hover за курсором для темы liquid-glass
    useEffect(() => {
        const theme = document.documentElement.getAttribute('data-theme');
        if (theme !== 'liquid-glass' || isMobile) {
            // Если тема не liquid-glass, удаляем элемент если он есть
            if (navContentRef.current) {
                const morphElement = navContentRef.current.querySelector(`.${s.morphIndicator}`);
                if (morphElement && morphElement.parentNode) {
                    morphElement.parentNode.removeChild(morphElement);
                }
            }
            return;
        }
        if (!navContentRef.current) return;

        let rafId = null;
        let morphElement = null;
        let isInitialized = false;

        // Создаем элемент-индикатор для эффекта морфинга
        morphElement = navContentRef.current.querySelector(`.${s.morphIndicator}`);
        if (!morphElement) {
            morphElement = document.createElement('div');
            morphElement.className = s.morphIndicator;
            navContentRef.current.appendChild(morphElement);
        }

        const updateMorph = () => {
            if (!morphElement || !navContentRef.current) {
                rafId = requestAnimationFrame(updateMorph);
                return;
            }
            
            const state = morphStateRef.current;
            // Плавная интерполяция для морфинга - всегда работает независимо
            // При движении мыши используем одну скорость, при переключении табов - другую
            const speed = morphStateRef.current.isMouseOver ? 0.25 : 0.3;
            state.currentY += (state.targetY - state.currentY) * speed;
            state.currentHeight += (state.targetHeight - state.currentHeight) * speed;
            
            morphElement.style.transform = `translateY(${state.currentY}px)`;
            morphElement.style.height = `${state.currentHeight}px`;
            
            // Эффект всегда виден, если мышь над меню или есть активный таб
            if (morphStateRef.current.isMouseOver || navContentRef.current.querySelector(`.${s.navLink}.${s.active}`)) {
                morphElement.style.opacity = '1';
            }
            
            // Всегда продолжаем анимацию - независимо от других процессов
            rafId = requestAnimationFrame(updateMorph);
        };

        const updateActiveTab = () => {
            if (!navContentRef.current || !morphElement || morphStateRef.current.isMouseOver) return;
            
            const navRect = navContentRef.current.getBoundingClientRect();
            const activeLink = navContentRef.current.querySelector(`.${s.navLink}.${s.active}`);
            
            if (activeLink) {
                const linkRect = activeLink.getBoundingClientRect();
                morphStateRef.current.targetY = linkRect.top - navRect.top;
                morphStateRef.current.targetHeight = linkRect.height;
                morphElement.style.opacity = '1';
            } else {
                morphElement.style.opacity = '0';
            }
        };

        const handleMouseMove = (e) => {
            if (!navContentRef.current || !morphElement) return;
            
            // Сохраняем позицию мыши для проверки
            lastMousePositionRef.current = { x: e.clientX, y: e.clientY };
            
            // Обновляем время последнего движения мыши
            lastMouseMoveTimeRef.current = Date.now();
            
            // Устанавливаем флаг сразу - движение мыши имеет приоритет
            morphStateRef.current.isMouseOver = true;
            
            // Всегда показываем эффект при движении мыши - независимо от других процессов
            morphElement.style.opacity = '1';
            
            const navRect = navContentRef.current.getBoundingClientRect();
            const mouseY = e.clientY - navRect.top;
            
            const navLinks = navContentRef.current.querySelectorAll(`.${s.navLink}`);
            let closestLink = null;
            let minDistance = Infinity;
            
            navLinks.forEach((link) => {
                const rect = link.getBoundingClientRect();
                const linkCenterY = rect.top + rect.height / 2 - navRect.top;
                const distance = Math.abs(mouseY - linkCenterY);
                
                if (e.clientY >= rect.top && e.clientY <= rect.bottom && 
                    e.clientX >= rect.left && e.clientX <= rect.right) {
                    if (distance < minDistance) {
                        minDistance = distance;
                        closestLink = link;
                    }
                }
            });
            
            if (closestLink) {
                const linkRect = closestLink.getBoundingClientRect();
                // Обновляем позицию МГНОВЕННО, без задержек и проверок
                // Это должно работать всегда и независимо
                morphStateRef.current.targetY = linkRect.top - navRect.top;
                morphStateRef.current.targetHeight = linkRect.height;
            }
        };

        const handleMouseLeave = () => {
            morphStateRef.current.isMouseOver = false;
            updateActiveTab();
        };

        const handleMouseEnter = () => {
            if (morphElement && navContentRef.current) {
                // При входе мыши в меню всегда показываем эффект
                morphElement.style.opacity = '1';
                // Если эффект еще не инициализирован, инициализируем его на первом элементе
                if (!isInitialized) {
                    const navRect = navContentRef.current.getBoundingClientRect();
                    const firstLink = navContentRef.current.querySelector(`.${s.navLink}`);
                    if (firstLink) {
                        const linkRect = firstLink.getBoundingClientRect();
                        morphStateRef.current.currentY = linkRect.top - navRect.top;
                        morphStateRef.current.currentHeight = linkRect.height;
                        morphStateRef.current.targetY = linkRect.top - navRect.top;
                        morphStateRef.current.targetHeight = linkRect.height;
                        morphElement.style.transform = `translateY(${morphStateRef.current.currentY}px)`;
                        morphElement.style.height = `${morphStateRef.current.currentHeight}px`;
                    }
                }
            }
        };

        // Инициализация позиции на активном табе
        const initPosition = () => {
            if (!navContentRef.current || !morphElement) return;
            
            const navRect = navContentRef.current.getBoundingClientRect();
            // Ищем активный таб - он всегда должен быть (Dashboard по умолчанию)
            let activeLink = navContentRef.current.querySelector(`.${s.navLink}.${s.active}`);
            
            // Если активный таб не найден, ищем Dashboard или первый элемент
            if (!activeLink) {
                // Пробуем найти Dashboard по data-menu-id или тексту
                const allLinks = navContentRef.current.querySelectorAll(`.${s.navLink}`);
                activeLink = Array.from(allLinks).find(link => {
                    const menuId = link.getAttribute('data-menu-id');
                    return menuId === 'dashboard' || link.textContent?.includes('Dashboard') || link.textContent?.includes('Дашборд');
                }) || allLinks[0];
            }
            
            if (activeLink) {
                const linkRect = activeLink.getBoundingClientRect();
                const targetY = linkRect.top - navRect.top;
                const targetHeight = linkRect.height;
                
                // Устанавливаем позицию сразу без интерполяции при инициализации
                morphStateRef.current.currentY = targetY;
                morphStateRef.current.currentHeight = targetHeight;
                morphStateRef.current.targetY = targetY;
                morphStateRef.current.targetHeight = targetHeight;
                
                morphElement.style.transform = `translateY(${targetY}px)`;
                morphElement.style.height = `${targetHeight}px`;
                morphElement.style.opacity = '1'; // Всегда показываем эффект
                isInitialized = true;
            }
        };

        // Инициализируем позицию сразу
        initPosition();
        const initTimeout = setTimeout(initPosition, 50);
        const initTimeout2 = setTimeout(initPosition, 150);
        const initTimeout3 = setTimeout(initPosition, 300);
        
        // Запускаем анимацию СРАЗУ - она должна работать независимо и непрерывно
        // Не ждем никаких других процессов - анимация работает в своем "ядре"
        updateMorph();
        
        // Добавляем обработчики событий
        navContentRef.current.addEventListener('mousemove', handleMouseMove);
        navContentRef.current.addEventListener('mouseleave', handleMouseLeave);
        navContentRef.current.addEventListener('mouseenter', handleMouseEnter);

        return () => {
            clearTimeout(initTimeout);
            clearTimeout(initTimeout2);
            clearTimeout(initTimeout3);
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            if (navContentRef.current) {
                navContentRef.current.removeEventListener('mousemove', handleMouseMove);
                navContentRef.current.removeEventListener('mouseleave', handleMouseLeave);
                navContentRef.current.removeEventListener('mouseenter', handleMouseEnter);
            }
            if (morphElement && morphElement.parentNode) {
                morphElement.parentNode.removeChild(morphElement);
            }
        };
    }, [isMobile, s, showExactMenu, showChat]);
    
    // Отслеживаем изменения темы через MutationObserver для переинициализации эффекта
    useEffect(() => {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
                    const newTheme = document.documentElement.getAttribute('data-theme');
                    // Принудительно перезапускаем эффект при изменении темы
                    if (newTheme === 'liquid-glass' && !isMobile) {
                        // Небольшая задержка для гарантии, что DOM обновился
                        setTimeout(() => {
                            // Триггерим переинициализацию через изменение зависимостей
                            // Просто обновляем состояние, чтобы эффект перезапустился
                            if (navContentRef.current) {
                                const morphElement = navContentRef.current.querySelector(`.${s.morphIndicator}`);
                                if (!morphElement) {
                                    // Если элемента нет, эффект должен переинициализироваться
                                    // через основной useEffect при следующем рендере
                                    return;
                                }
                                // Если элемент есть, переинициализируем позицию
                                const navRect = navContentRef.current.getBoundingClientRect();
                                const activeLink = navContentRef.current.querySelector(`.${s.navLink}.${s.active}`);
                                if (activeLink) {
                                    const linkRect = activeLink.getBoundingClientRect();
                                    morphStateRef.current.currentY = linkRect.top - navRect.top;
                                    morphStateRef.current.currentHeight = linkRect.height;
                                    morphStateRef.current.targetY = linkRect.top - navRect.top;
                                    morphStateRef.current.targetHeight = linkRect.height;
                                    morphElement.style.transform = `translateY(${linkRect.top - navRect.top}px)`;
                                    morphElement.style.height = `${linkRect.height}px`;
                                    morphElement.style.opacity = '1';
                                }
                            }
                        }, 100);
                    }
                }
            });
        });
        
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme']
        });
        
        return () => {
            observer.disconnect();
        };
    }, [isMobile, s]);

    // Отслеживаем изменение активного таба и устанавливаем позицию эффекта
    // ТОЛЬКО если мышь не над меню и не двигалась недавно
    useEffect(() => {
        const theme = document.documentElement.getAttribute('data-theme');
        if (theme !== 'liquid-glass' || isMobile) return;

        const morphElement = navContentRef.current?.querySelector(`.${s.morphIndicator}`);
        if (!morphElement || !navContentRef.current) return;
        
        // Проверяем физическое положение мыши над меню по последней сохраненной позиции
        const checkMouseOverMenu = () => {
            if (!navContentRef.current || !lastMousePositionRef.current) return false;
            const navRect = navContentRef.current.getBoundingClientRect();
            const mouseX = lastMousePositionRef.current.x;
            const mouseY = lastMousePositionRef.current.y;
            // Проверяем, находится ли последняя позиция мыши над навигацией
            return mouseX >= navRect.left && mouseX <= navRect.right && 
                   mouseY >= navRect.top && mouseY <= navRect.bottom;
        };
        
        // Проверяем, не двигалась ли мышь недавно (в течение последних 2000ms)
        // Большое время для полного исключения конфликтов
        const timeSinceLastMouseMove = Date.now() - lastMouseMoveTimeRef.current;
        
        // Проверяем физическое положение мыши
        const isMousePhysicallyOverMenu = checkMouseOverMenu();
        
        // Обновляем позицию на активный таб ТОЛЬКО если:
        // 1. Мышь не над меню (ни по флагу, ни физически)
        // 2. Мышь не двигалась недавно (более 2000ms назад)
        // Это гарантирует, что обновление не произойдет, если пользователь активно двигает мышью
        if (!morphStateRef.current.isMouseOver && !isMousePhysicallyOverMenu && timeSinceLastMouseMove >= 2000) {
            // Используем requestAnimationFrame для синхронизации с рендерингом
            let rafId = null;
            
            const updatePosition = () => {
                // Проверяем еще раз перед обновлением - критически важно!
                const timeSinceLastMouseMove = Date.now() - lastMouseMoveTimeRef.current;
                const isMousePhysicallyOverMenuNow = checkMouseOverMenu();
                
                // НЕ обновляем, если:
                // 1. Мышь над меню (по флагу или физически)
                // 2. Мышь двигалась недавно (менее 2000ms назад)
                if (morphStateRef.current.isMouseOver || isMousePhysicallyOverMenuNow || timeSinceLastMouseMove < 2000 || !navContentRef.current) {
                    return;
                }
                
                const navRect = navContentRef.current.getBoundingClientRect();
                const activeLink = navContentRef.current.querySelector(`.${s.navLink}.${s.active}`);
                
                if (activeLink) {
                    const linkRect = activeLink.getBoundingClientRect();
                    // Обновляем целевую позицию ТОЛЬКО если мышь точно не двигается
                    morphStateRef.current.targetY = linkRect.top - navRect.top;
                    morphStateRef.current.targetHeight = linkRect.height;
                    morphElement.style.opacity = '1';
                } else {
                    morphElement.style.opacity = '0';
                }
            };
            
            // Используем двойной requestAnimationFrame с дополнительной задержкой
            // для гарантии, что пользователь успел начать двигать мышью
            rafId = requestAnimationFrame(() => {
                rafId = requestAnimationFrame(() => {
                    // Еще одна проверка перед обновлением
                    const timeSinceLastMouseMove = Date.now() - lastMouseMoveTimeRef.current;
                    const isMousePhysicallyOverMenuNow = checkMouseOverMenu();
                    if (!morphStateRef.current.isMouseOver && !isMousePhysicallyOverMenuNow && timeSinceLastMouseMove >= 2000) {
                        updatePosition();
                    }
                });
            });
            
            return () => {
                if (rafId) {
                    cancelAnimationFrame(rafId);
                }
            };
        }
    }, [showExactMenu, showChat, isMobile, s]);

    const handleChatClick = () => {
        setShowChat(!showChat);
    };

    const handleCollapseToggle = () => {
        // На мобильных устройствах управляем видимостью меню
        if (isMobile) {
            setIsMobileOpen(prev => !prev);
        } else {
            // На десктопе управляем сворачиванием
            const newCollapsedState = !isCollapsed;
            setIsCollapsed(newCollapsedState);
            if (onCollapseChange) {
                onCollapseChange(newCollapsedState);
            }
        }
    };

    // Закрываем мобильное меню при клике на пункт
    const handleMenuItemClick = (action) => {
        if (action) action();
        if (isMobile) {
            setIsMobileOpen(false);
        }
    };

    // Пересоздаем menuItems при изменении языка
    const menuItems = React.useMemo(() => [
        { id: 'dashboard', icon: dashboardImg, label: t('navbar.dashboard', 'Главная'), action: () => setShowExactMenu('dashboard') },
        { id: 'trade', icon: trade, label: t('navbar.trading_section', 'Торговля'), action: () => setShowExactMenu('trade') },
        { id: 'markets', icon: markets, label: t('navbar.markets', 'Рынки'), action: null },
        { id: 'wallet', icon: wallet, label: t('navbar.wallet', 'Кошелек'), action: () => setShowExactMenu('wallet') },
        { id: 'broker', icon: broker, label: t('navbar.broker', 'Брокер'), action: () => setShowExactMenu('broker') },
        { id: 'mailbox', icon: mailbox, label: t('navbar.mailbox', 'Почта'), action: () => setShowExactMenu('mailbox') },
        { id: 'settings', icon: setting, label: t('navbar.settings', 'Настройки'), action: () => setShowExactMenu('settings') },
    ], [t, siteSettings?.currentLanguage]);

    const bottomItems = React.useMemo(() => [
        { id: 'region', label: t('navbar.region', 'Регион'), action: null },
        { id: 'support', icon: supportChat, label: t('navbar.support', 'Поддержка'), action: handleChatClick },
        { 
            id: 'logout', 
            icon: exit, 
            label: t('navbar.logout', 'Выйти'), 
            action: userRole === 'sap' ? null : () => {
            console.log('Logout clicked, onOpenSiteSettings:', onOpenSiteSettings);
            onOpenSiteSettings && onOpenSiteSettings();
            }
        },
    ], [t, siteSettings?.currentLanguage, siteSettings?.selectedFlag, userRole, onOpenSiteSettings]);

    return (
        <>
            {/* Mobile Overlay */}
            {isMobileOpen && (
                <div 
                    className={s.mobileOverlay}
                    onClick={() => setIsMobileOpen(false)}
                />
            )}
            
            <div 
                ref={navbarRef}
                className={`${s.navbar} ${isCollapsed ? s.collapsed : ''} ${isMobileOpen ? s.open : ''}`}
            >
                <div className={s.navHeader}>
                    <div
                        className={s.logoContainer}
                        style={(() => {
                            const g = siteSettings?.siteTitleLogoGap;
                            if (!g || !String(g).trim()) return undefined;
                            const v = String(g).trim();
                            return { gap: (!isNaN(parseFloat(v)) && !v.match(/px|em|rem|pt|%/)) ? v + 'px' : v };
                        })()}
                    >
                        {isCollapsed && !isMobileOpen ? (
                            // В свернутом состоянии показываем paint.png как кнопку разворачивания
                        <img 
                                className={s.paintLogo} 
                                src={paint} 
                                alt="expand menu" 
                                onClick={handleCollapseToggle}
                                style={{ cursor: 'pointer' }}
                            />
                        ) : (
                            // В развернутом состоянии показываем paint.png + текст названия биржи
                            <>
                                <img 
                                    className={`${s.paintLogo} ${s.noHover}`}
                                    src={siteSettings?.siteLogo 
                                        ? (siteSettings.siteLogo.startsWith('/') || siteSettings.siteLogo.startsWith('http') 
                                            ? siteSettings.siteLogo 
                                            : `/logo/${encodeURIComponent(siteSettings.siteLogo)}`)
                                        : paint} 
                            alt="logo" 
                                    onError={(e) => {
                                        console.error('[NavBar] Ошибка загрузки логотипа:', e.target.src);
                                        if (!e.target.src.includes('/data/logo/')) {
                                            const filename = siteSettings?.siteLogo;
                                            if (filename) {
                                                e.target.src = `/data/logo/${encodeURIComponent(filename)}`;
                                            } else {
                                                e.target.src = paint;
                                            }
                                        } else {
                                            e.target.src = paint;
                                        }
                                    }}
                                />
                                <span 
                                    className={s.brandText}
                                    style={{ 
                                        fontFamily: siteSettings?.siteTitleFont || "'Inter', sans-serif",
                                        fontWeight: siteSettings?.siteTitleFontWeight || '400',
                                        fontStyle: siteSettings?.siteTitleFontStyle || 'normal',
                                        letterSpacing: (() => {
                                            const spacing = siteSettings?.siteTitleLetterSpacing;
                                            if (spacing && spacing.trim() !== '') {
                                                const value = spacing.trim();
                                                // Если число без единиц, добавляем px
                                                if (!isNaN(parseFloat(value)) && !value.match(/px|em|rem|pt|%/)) {
                                                    return value + 'px';
                                                }
                                                return value;
                                            }
                                            return 'normal';
                                        })(),
                                        fontSize: (() => {
                                            const size = siteSettings?.siteTitleFontSize;
                                            if (size && size.trim() !== '') {
                                                const value = size.trim();
                                                // Если число без единиц, добавляем px
                                                if (!isNaN(parseFloat(value)) && !value.match(/px|em|rem|pt|%/)) {
                                                    return value + 'px';
                                                }
                                                return value;
                                            }
                                            return undefined;
                                        })()
                                    }}
                                >
                                    {siteSettings?.siteName || '暗号交換'}
                                </span>
                            </>
                        )}
            </div>
                    {/* Кнопка сворачивания показывается только в развернутом состоянии */}
                    {(!isCollapsed || isMobileOpen) && (
                    <button 
                        className={s.collapseBtn}
                        onClick={handleCollapseToggle}
                        aria-label="Toggle navigation"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path 
                                d={isMobile 
                                    ? (isMobileOpen ? "M18 6L6 18M6 6l12 12" : "M3 12h18M3 6h18M3 18h18")
                                        : "M15 18l-6-6 6-6"
                                } 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                />
                        </svg>
                    </button>
                    )}
            </div>

                <nav ref={navContentRef} className={s.navContent}>
                    <ul className={s.navList}>
                        {menuItems.map((item) => (
                            <li key={item.id} className={s.navItem}>
                                <button
                                    data-menu-id={item.id}
                                    onClick={() => handleMenuItemClick(item.action)}
                                    className={`${s.navLink} ${
                                        (showExactMenu === item.id || (item.id === 'support' && showChat)) 
                                            ? s.active : ''
                                    }`}
                                    title={isCollapsed && !isMobileOpen ? item.label : ''}
                                >
                                    <div className={s.iconWrapper}>
                                        {item.id === 'region' ? (
                                            <FlagIcon siteSettings={siteSettings} className={s.regionIcon} alt="Region" />
                                        ) : (
                                            <img 
                                                src={item.icon} 
                                                alt="" 
                                                className={s.navIcon} 
                                            />
                                        )}
            </div>
                                    {(!isCollapsed || isMobileOpen) && <span className={s.navLabel}>{item.label}</span>}
                                </button>
                            </li>
                        ))}
                    </ul>

                    <div className={s.navDivider}></div>

                    <ul className={s.navList}>
                        {bottomItems.map((item) => (
                            <li key={item.id} className={s.navItem}>
                                <button
                                    data-menu-id={item.id}
                                    onClick={() => {
                                        if (item.action) {
                                            handleMenuItemClick(item.action);
                                        }
                                    }}
                                    className={`${s.navLink} ${
                                        (item.id === 'support' && showChat) ? s.active : ''
                                    }`}
                                    title={isCollapsed && !isMobileOpen ? item.label : ''}
                                    style={!item.action ? { pointerEvents: 'none', cursor: 'default' } : {}}
                                >
                                    <div className={s.iconWrapper}>
                                        {item.id === 'region' ? (
                                            <FlagIcon siteSettings={siteSettings} className={s.regionIcon} alt="Region" />
                                        ) : (
                                            <img 
                                                src={item.icon} 
                                                alt="" 
                                                className={s.navIcon} 
                                            />
                                        )}
            </div>
                                    {(!isCollapsed || isMobileOpen) && <span className={s.navLabel}>{item.label}</span>}
                                </button>
                            </li>
                        ))}
                    </ul>
                </nav>
            </div>
        </>
    );
});

export { NavBar };